// @flow
import React, { useState } from "react";
import {reisy, use} from "reisy";
import {injectIntl, defineMessages} from "react-intl";
import mapKey, {OUT} from "../components/mapkey.js";
import * as Icons from "../components/icons.js";
import Link from "../components/link.js";
import SearchButton from "../components/searchbutton.js";
import RealMenu from "../components/menu.js";
import TagNav from "../components/tagnav.js";
import Image from "../components/image.js";
import Dropdown from "./dropdown.js";
import LikeButton from '../components/like';
import type Player from "../player/player.js";
import LOCALES from "../locales/index.js";
import {post} from "../xhr.js";
import {scrollbarSize, isMobile, isIOS} from "../environment.js";

export const styles = reisy`
@namespace Navigation;

itemHeight: 40px;

Spacer {
	flex: 1 1 auto;
}

Item {
	display: flex;
	align-items: center;
	flex: 0 0 $itemHeight;
	position: relative;
	box-sizing: content-box;
	height: $itemHeight;
	padding: 8px;
	margin: 8px 4px;
	font-size: 24px;
	line-height: 1;
	border: 0;
	outline: 0;
	background: transparent;
	pointer-events: all;
	color: $Foreground;

	transition: all $Consts.Transition;

	font-family: $Fonts.aside;
	text-decoration: none;

	svg:not([width]) {
		width: 100%;
	}

	path, circle {
		transition: all $Consts.Transition;
		fill: $Foreground;
		stroke: $Foreground;
	}


	$Transparent & {
		color: $ColorNav;
		text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
		path, circle {
			fill: $ColorNav;
			stroke: $ColorNav;
		}
	}
	$Navigation.DarkMode & {
		color: $ColorNavLight;
		text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
		path, circle {
			fill: $ColorNavLight;
			stroke: $ColorNavLight;
		}
	}
}

Placeholder {
	@extends Item;
	pointer-events: inherit;
}

Button {
	@extends Item;

	cursor: pointer;
	justify-content: center;

	svg {
		width: 24px;
		height: 24px;
	}

	$Transparent & {
		svg {
			filter: drop-shadow(1px 1px 0 rgba(255, 255, 255, 0.6))
		}
	}
	$Navigation.DarkMode & {
		svg {
			filter: drop-shadow(1px 1px 0 rgba(0, 0, 0, 0.3))
		}
	}

	&[data-navbar-item*='expand'] {
		$Media.medium {
			display: none;
		}
	}
}

@keyframes buttonprogressanim {
	0% {
		opacity: 1;
		transition-timing-function: ease-in-out;
	}
	50% {
		opacity: 0.5;
		transition-timing-function: ease-in-out;
	}
	100% {
		opacity: 1;
		transition-timing-function: ease-in-out;
	}
}

ButtonProgress {
	animation: $Navigation.buttonprogressanim .75s linear infinite;
}

ZoomItem {
	@extends Button;
}

Placeholder {
	@extends Item;
	pointer-events: none;
}

PublisherLogo {
	@extends Item;
	padding: 0;
	height: $itemHeight;
	display: inline-flex;
	flex: 0 0 auto;

	img {
		max-height: 1.5em;
	}
}

PagestripLogo {
	@extends Item;
	svg {
		height: 100%;
		stroke-width: 0;
	}
}

PublisherName {
	@extends Item;

	display: inline-block;
	font-size: 16px;
	line-height: 24px;

	flex-basis: auto;

	color: inherit;
}

PublisherSlogan {
	@extends PublisherName;
}

PublisherDescription {
	@extends PublisherName;
}

Pagestrip {
	@extends Item;
	font-size: 16px;
	line-height: 24px;
	font-family: $Fonts.pagestrip;

	$PagestripLogo + & {
		padding-left: 0;
		margin-left: -4px;
	}

	&:hover {
		text-decoration: underline;
	}
}

Search {
	@extends Item;

	flex: 0 1 auto;
	min-width: 5em;
	font-size: 16px;
	padding: 4px 8px;
	height: $itemHeight;

	$Collapsed & {
		$Media.ltSmall {
			display: none;
		}
	}
}

SearchToggle {
	form {
		min-width: 10em;
		padding: 4px 8px;
	}

	$Collapsed & {
		$Media.ltSmall {
			display: none;
		}
	}
}

SearchToggleButton {
	@extends Item;
}

Menu {
	@extends Item;

	flex-basis: auto;
	font-size: 16px;
	line-height: 24px;

	$MenuTop.listLink {
		color: inherit;
	}

	$Expanded & {
		display: flex;
		height: auto;
	}

	$Media.ltMedium {
		display: none;
	}
}

Tags {
	@extends Menu;

	flex-basis: 100%;
	display: flex;
	justify-content: center;
}

TagButton {
	@extends Button;
}

TagButtonHighlighted {
	@extends TagButton;
}

TagDropdownActions {

}

TagSelect {
  font-family: kent-font-aside-override, kent-font-aside, sans-serif;
}

TagReset {
  font-family: kent-font-aside-override, kent-font-aside, sans-serif;
  text-decoration: none;
}

TagSectionTitle {
	font-family: kent-font-aside-override, kent-font-aside, sans-serif;
	cursor: pointer;
	appearance: none;
	outline: 0;
	border: 0;
	box-shadow: none;
	display: block;
	font-size: 1em;
	width: 100%;
	text-align: left;
}

TagApply {
	font-family: kent-font-aside-override, kent-font-aside, sans-serif;
	cursor: pointer;
	appearance: none;
	border: 0;
	font-size: 1em;
	background: transparent;
	color: #fff;
}

TagSectionTitleClosed {
	@extends TagSectionTitle;
	& + * {
		display: none;
	}
}

LocaleButton {
	font-size: 16px;
	text-transform: uppercase;
	width: $itemHeight;
}
LocaleList {
	list-style: none;
	font-size: 1em;
	font-family: $Fonts.aside;
}
LocaleListCompact {
	@extends LocaleList;
	margin: 0;
	padding: 0;
}
LocaleItem {
	display: flex;
	&:not(:first-child)::before {
		content: '|';
		font-weight: normal;
	}
}
LocaleSelectedItem {
	font-weight: bold;
}
LocaleLink {
	display: block;
	padding: 0 8px;
	color: inherit;
	white-space: nowrap;
	text-decoration: none;
	pointer-events: all;

	& > * {
		pointer-events: none;
	}

	&:hover {
		text-decoration: underline;
	}
}
LocaleName {
}
LocaleCode {
	display: none;
}

ShowPDF {
	@extends Button;
}
`;

const messages = defineMessages({
	back: {
		id: "navigation.back",
		defaultMessage: "Back",
	},
	showpdf: {
		id: "navigation.showpdf",
		defaultMessage: "Show PDF",
	}
});

class _Back extends React.Component {
	link: HTMLElement;
	setLink = (el: HTMLAnchorElement) => (this.link = el);

	props: {href: string};

	componentDidMount() {
		if (!IS_BROWSER) { return; }
		document.addEventListener("keydown", this.handleKeydown);
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.handleKeydown);
	}

	handleKeydown = (ev: KeyboardEvent) => {
		const key = mapKey(ev);
		if (OUT.includes(key)) {
			// We still preventDefault here, since some browsers set Backspace to
			// history back, which triggers navigation. So make sure to prevent that.
			ev.stopPropagation();
			ev.preventDefault();
			const isFullscreen = document.fullscreenElement ||
				document.webkitFullscreenElement ||
				document.mozFullScreenElement ||
				// $FlowFixMe: yes, that prefix is a thing
				document.msFullscreenElement;
			if (!isFullscreen) {
				this.link.click();
			}
		}
	}

	render() {
		const Icon = this.props.icon || Icons.Back;

		return (
			<Link data-navbar-item="back" _ref={this.setLink} {...use(styles.Button)} to={this.props.href} title={this.props.intl.formatMessage(messages.back)}>
				<Icon role="img" aria-label={this.props.intl.formatMessage(messages.back)} />
			</Link>
		);
	}
}
export const Back = injectIntl(_Back);

export const Spacer = () => (<div data-navbar-item="spacer" {...use(styles.Spacer)} />);

export const Button = ({item, children, onClick, onMouseEnter, onTouchStart, style, className, title = ''}: any) => (
	<button data-navbar-item={item} {...use(styles.Button, style, className)} onClick={onClick} onMouseEnter={onMouseEnter} onTouchStart={onTouchStart} title={title}>
		{children}
	</button>
);

export const Like = ({content, style}: any) => (
	<LikeButton publishable={content} noText style={styles.Button} />
);

export const PublisherTitle = ({publisher}) => (
	<Link
		data-navbar-item="pagestrip"
		{...use(styles.PublisherName)}
		mode="native"
		to={{context: publisher.id, absolute: '/', hierarchy: [], query: {}}}>
			{publisher.title}
	</Link>
);

export const PublisherName = ({publisher}) => (
	<Link
		data-navbar-item="pagestrip"
		{...use(styles.PublisherName)}
		mode="native"
		to={{context: publisher.id, absolute: '/', hierarchy: [], query: {}}}>
			{publisher.name}
	</Link>
);

export const PublisherSlogan = ({publisher}) => (
	<div
		data-navbar-item="pagestrip"
		{...use(styles.PublisherSlogan)}>
		{publisher.slogan}
	</div>
);

export const PublisherDescription = ({publisher}) => (
	<div
		data-navbar-item="pagestrip"
		{...use(styles.PublisherDescription)}>
		{publisher.description}
	</div>
);

export const PublisherLogo = ({publisher}) => {
	const image = publisher.image;

	return (
		image
		? <Link
			data-navbar-item="publisherlogo"
			{...use(styles.PublisherLogo)}
			mode="native"
			to={{context: publisher.id, absolute: '/', hierarchy: [], query: {}}}>
				<Image image={image} name="fitted" availWidth={300} alt={`${publisher.name} Logo`} />
			</Link>
		: <PublisherName publisher={publisher} />
	);
};

export const PagestripLogo = () => (
	<a
		data-navbar-item="pagestriplogo"
		{...use(styles.PagestripLogo)}
		href="https://pagestrip.com/">
		<Icons.Logo />
	</a>
);

export const Pagestrip = () => (
	<a
		data-navbar-item="pagestrip"
		{...use(styles.Pagestrip)}
		href="https://pagestrip.com/">
		pagestrip
	</a>
);

export const Search = () => (
	<div data-navbar-item="search" {...use(styles.Search)}>
		<SearchButton />
	</div>
);

export const SearchToggle = () =>
	<Dropdown
		item="search-toggle"
		className={styles.SearchToggle}
		button={<Icons.MagnifyingGlass {...use(styles.SearchToggleButton)} />}>
		<SearchButton />
	</Dropdown>;

export const Menu = ({player, menuid, onItemClick}: {player: Player, menuid?: any, onItemClick?: any}) => {
	let publisher = player.content || player.publisher;
	if (!publisher && !player.options.menues) { return null; }
	publisher = publisher || {};
	let active;
	if (publisher.type === "story" || publisher.type === "collection") {
		publisher = publisher.publisher;
		active = player.content.id;
	}
	
  const tags = player.url.query.tags || player.url.query.t;

	return (
		<div data-navbar-item="menu" {...use(styles.Menu)}>
			<RealMenu 
				position="top" 
				active={active} 
				menues={player.options.menues || publisher.menues} 
				menuid={menuid} 
				onItemClick={onItemClick} 
				selectedTags={tags && tags.length ? tags.split(',') : []} 
			/>
		</div>
	);
};

export const Tags = ({player}: {player: Player}) => {
	let publisher = player.content || player.publisher;
	if (!publisher && !player.options.menues) { return null; }
	let active;
	if (publisher.type === "story" || publisher.type === "collection") {
		publisher = publisher.publisher;
		active = player.content.id;
	}
	const tags = player.url.query.tags || player.url.query.t;
	const menu = (player.options.menues || publisher.menues)['tags.top'];
	if (!menu || !menu.length) { return null; }
	return (
		<div data-navbar-item="menu" {...use(styles.Tags)}>
			<TagNav tags={menu[0].items} selected={tags && tags.length ? tags.split(',') : []} active={active} />
		</div>
	);
};

const removeBodyScroll = (open) => {
	if (!IS_BROWSER || scrollbarSize) { return }
	document.body.style.overflow = open && isMobile ? 'hidden' : '';
	if(isIOS) {
		document.body.style.position = open && isMobile ? 'fixed' : '';
		document.body.style.width = open && isMobile ? '100vw' : '';
	} 
}

export const TagSelect = ({player, menuIds}: {player: Player}) => {
	let publisher = player.content;
	if (!publisher && !player.options.menues) { return null; }
	let active;
	if (publisher.type === "story" || publisher.type === "collection") {
		publisher = publisher.publisher;
		active = player.content.id;
	}
	const tags = (player.url.query.tags || '').split(',').filter(Boolean);
	const menu = (player.options.menues || publisher.menues)['tags.top'];
	if (!menu || !menu.length) { return null; }
	const menus = menu.filter(m => ~menuIds.indexOf(m.id))

	return (
		<Dropdown
			item="tagselect"
			{...use(styles.TagSelect)}
			onChange={removeBodyScroll}
			button={<Button {...use(tags.length ? styles.TagButtonHighlighted : styles.TagButton)}><span>{tags.length ? "Gefiltert" : "Themenfilter"}</span></Button>}>
			<TagDropdown menus={menus} tags={tags} active={active} />
		</Dropdown>
	);
};

const TagDropdown = ({menus, tags, active, onClose}) => (<div>
	<div {...use(styles.TagDropdownActions)}>
		<Link {...use(styles.TagReset)} to={{query: {tags: ''}}}>
			Zurücksetzen
		</Link>
		<TagApply onClose={onClose} />
	</div>
	<div>
	{menus.map((m,  i) => {
		const options = [...new Set(m.items.reduce((acc, t) => acc.concat(t.tags), []))]
		return (<div key={m.id}>
			<TagSectionTitle title={m.title} open={true} />
			<TagNav allToggle={true} tags={m.items} selected={tags} options={options} active={active} />
		</div>)
	})}
	</div>
</div>)

class TagSectionTitle extends React.Component {
	constructor(props) {
		super(props)
		this.state = { open: !!props.open }
	}

	_onClick = () => this.setState({ open: !this.state.open })
	
	render() {
		return <button onClick={this._onClick} {...use(this.state.open ? styles.TagSectionTitle : styles.TagSectionTitleClosed)}>{this.props.title}</button>
	}
}

const TagApply = ({onClose}) => (<button onClick={onClose} {...use(styles.TagApply)}>Filtern</button>)

async function changeLanguage(player: Player, locale: string, ev: Event) {
	ev.preventDefault();
	const {href} = (ev.currentTarget: any);
	if (!player.options.jackInTheBox) {
		try {
			await post("/i18n/setlang/", `language=${locale}`);
		} catch (e) {}
	}
	document.location.href = href;
}

export const Locale = ({player, translations}: {player: Player, translations?: any}) => {
	const {locale} = player.options;
	const locales = LOCALES;
	const showLocales = player.publisher && player.publisher.locales || [];
	if (!showLocales.length) {
		showLocales.push(...(translations && Object.keys(translations).filter(k => k !== 'default') || Object.keys(LOCALES)));
	}

	const links = showLocales.map(loc => {
			const _locale = loc.code || loc;
			if (translations && translations[_locale]) {
				const t = translations[_locale] || translations.default;
				const translatedHierarchy = player.url.hierarchy.map(i => {
					const publishable = player.api.resolveSync(i);
					const t = publishable && publishable.translations && (publishable.translations[_locale] || publishable.translations.default);
					return t && t.id || i;
				});
				return {hierarchy: translatedHierarchy, slug: t.slug, locale: _locale, query: { tags: undefined }}
			}
			return {locale: _locale, query: { tags: undefined }};
	});

	const items = showLocales.map((loc, idx) => {
		const _locale = loc.code || loc;
		return (
			<li key={_locale} {...use(styles.LocaleItem, _locale === locale && styles.LocaleSelectedItem)}>
				<Link
					{...use(styles.LocaleLink)}
					to={links[idx]}
					onClick={changeLanguage.bind(null, player, _locale)}>
					<span {...use(styles.LocaleName)}>{locales[_locale] && locales[_locale].name || _locale}</span> <span {...use(styles.LocaleCode)}>{_locale}</span>
				</Link>
			</li>
		)
	});
	return (
		<div data-navbar-item="locale">
			<ul {...use(styles.LocaleList, styles.Item)}>{items}</ul>
		</div>
	);
};

export const LocaleCompact = ({player, translations}: {player: Player, translations?: any}) => {
	const {locale} = player.options;
	const locales = LOCALES;
	const showLocales = player.publisher && player.publisher.locales || [];
	if (!showLocales.length) {
		showLocales.push(...(translations && Object.keys(translations).filter(k => k !== 'default') || Object.keys(LOCALES)));
	}

	const links = showLocales.map(loc => {
			const _locale = loc.code || loc;
			if (translations) {
				const t = translations[_locale] || translations.default;
				const translatedHierarchy = player.url.hierarchy.map(i => {
					const publishable = player.api.resolveSync(i);
					const t = publishable && publishable.translations && (publishable.translations[_locale] || publishable.translations.default);
					return t && t.id || i;
				});
				return {hierarchy: translatedHierarchy, slug: t.slug, locale: _locale, query: { tags: undefined }}
			}
			return {locale: _locale, query: { tags: undefined }};
	});

	const items = showLocales.map((loc, idx) => {
		const _locale = loc.code || loc;
		return (
			<li key={_locale} {...use(_locale === locale && styles.LocaleSelectedItem)}>
				<Link
					{...use(styles.LocaleLink)}
					to={links[idx]}
					onClick={changeLanguage.bind(null, player, _locale)}>
					<span>{loc.nameLocal || locales[_locale] && locales[_locale].name || _locale}</span> <span>({_locale})</span>
				</Link>
			</li>
	)});
	return (
		<Dropdown
			item="localecompact"
			button={<Button style={styles.LocaleButton}>{player.options.locale}</Button>}>
			<ul {...use(styles.LocaleListCompact)}>{items}</ul>
		</Dropdown>
	);
};

const zoomLabels = {
	'1': 'A',
	'1.5': 'A+',
	'2': 'A++',
}
export const Zoom = ({player, zoom}: {player: Player, zoom: any}) => {
	return (<Link
		{...use(styles.ZoomItem)}
		to={zoom && {zoom} || {}}>
		{zoomLabels[zoom || 1]}
	</Link>)
}

const _ShowPDF = ({player, player: { content: {options: { pdf, jackInTheBox } = {} } = {} } = {}, intl}) => (
	<Link
		data-navbar-item="pagestrip"
		{...use(styles.ShowPDF)}
		to={pdf ? pdf : jackInTheBox ? `${player.options.apiBase}pdf/?url=${encodeURIComponent(player.url.create({hierarchy: [player.content.id], query: {q: undefined, appearance: undefined}}, {absolute: player.options.apiBase, withContext: true}))}` : `${player.options.apiBase}pdf/?url=${encodeURIComponent(player.url.create({query: {q: undefined, appearance: undefined}}, {absolute: true}))}`}
		target="_blank"
		rel="nofollow"
		title={intl.formatMessage(messages.showpdf)}>
			<Icons.PDF role="img" aria-label={intl.formatMessage(messages.showpdf)} />
	</Link>
);
export const ShowPDF = injectIntl(_ShowPDF);

export {default as TagList} from '../components/taglist.js';

export const Logout = ({player}) => {

	const [progress, setProgress] = useState(false);

	const handleLogout = async () => {
		setProgress(true);

		const {url} = player;

		const publisherBaseUrl = url
			.create({}, {absolute: true})
			.replace(/https?:/g, '');

		post(`${publisherBaseUrl}authorize/logout/`).finally(() => {
			setProgress(false);
			window.location.reload();
		});
	}

	return <button {...use(styles.Button, progress ? styles.ButtonProgress : null)} onClick={handleLogout} title="logout">
		<Icons.Logout role="img" aria-label="logout" />
	</button>
}


// WEBPACK FOOTER //
// ./lib/navigation/items.js